'use client'

import { useEffect, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import './CargandoBcm.css'

/**
 * Pantalla de carga de las apps: el logo real de BCM animado, de unos 400 px, justo en
 * el medio de la pantalla, con «Cargando…» abajo. Trasladada desde bcm-dev
 * (bcm-dashboard/app/components/ui/CargandoBcm.tsx, elemento 55 del CSS Tester),
 * sincronizada hasta `c45e8625` (2026-10-13). Dos maneras:
 *
 * - `trazo`: se dibuja el contorno de cada letra, una tras otra, se rellena con sus
 *   colores y se desvanece. Es la de las apps.
 * - `neon`: las letras se prenden de a una, con el parpadeo de un tubo de neón.
 *
 * El SVG del logo se trae una vez y se mete en la página a mano, en una caja que React
 * no toca (con `dangerouslySetInnerHTML` React lo reescribía en cada render y la
 * animación volvía a cero). Cada letra es su propio grupo: así se anima letra por
 * letra. Sus clases e ids se renombran por instancia, para no pisar otros SVG de la
 * página.
 *
 * `pantalla` (por defecto) la pone en el medio de la pantalla, sobre todo; sin eso
 * queda en su lugar.
 *
 * Diferencia con el original: el texto sale de i18n (common:loading).
 */

let logoSvg = null
const traerLogo = () =>
  (logoSvg ??= fetch('/branding/logo.svg').then((r) => (r.ok ? r.text() : '')))

/** Marca cada letra (y cada punto) con su orden, y normaliza sus contornos. */
function marcarLetras(caja) {
  const svg = caja.querySelector('svg')
  if (!svg) return
  const capas = svg.querySelectorAll(':scope > g')
  capas[0]?.setAttribute('class', 'bcm-cargando-base')
  const letras = capas[1]?.querySelectorAll(':scope > g') ?? []
  letras.forEach((g, i) => {
    g.classList.add('bcm-letra')
    g.style.setProperty('--i', String(i))
    // El trazo mide 1 en cualquier forma: el dibujado va de 1 a 0.
    for (const forma of g.querySelectorAll('path, circle')) {
      forma.setAttribute('pathLength', '1')
    }
  })
}

export function CargandoBcm({ pantalla = true, variante = 'trazo' }) {
  const { t } = useTranslation()
  const id = useId().replace(/[^a-zA-Z0-9]/g, '')
  const logo = useRef(null)
  const halo = useRef(null)

  useEffect(() => {
    let vivo = true
    traerLogo().then((texto) => {
      if (!vivo || !texto) return
      const svg = texto
        .replace(/<\?xml[^>]*>/, '')
        .replace(/cls-/g, `bcm-${id}-`)
        .replace(/linear-gradient/g, `bcm-${id}-degrade`)
      for (const caja of [logo.current, halo.current]) {
        if (!caja) continue
        caja.innerHTML = svg
        marcarLetras(caja)
      }
    })
    return () => {
      vivo = false
    }
  }, [id, variante])

  const cargando = t('common:loading')

  return (
    <div
      className={pantalla ? 'bcm-cargando bcm-cargando--pantalla' : 'bcm-cargando'}
      role="status"
      aria-label={cargando}
    >
      <div
        className={`bcm-cargando-logo bcm-cargando--${variante}`}
        data-variante={variante}
        style={{ '--trazo': `url(#bcm-${id}-degrade)` }}
      >
        {/* El neón brilla con una copia desenfocada detrás. */}
        {variante === 'neon' && <div ref={halo} className="bcm-cargando-halo" aria-hidden />}
        <div ref={logo} />
        <div className="bcm-cargando-texto">{cargando}</div>
      </div>
    </div>
  )
}
